import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { Grid, HStack, Spacer, VStack } from '@/components/Layout';
import StepperHeaderComponent from '@/components/StepperHeader';
import { Text } from '@/components/Typography/Text';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { CertificationKind } from '../-constants';

import { AddCertificationStepKeys } from './-constants/constant';

export const Route = createFileRoute('/_staff/certification/add/')({
  component: Introduce,
});

type CommonProps = {
  selectedCertificationKind: CertificationKind | null;
  setSelectedCertificationKind: (kind: CertificationKind) => void;
};

function Introduce() {
  const [selectedCertificationKind, setSelectedCertificationKind] =
    useState<CertificationKind>(CertificationKind.BASE);

  return (
    <VStack className="mx-auto w-[1000px]">
      <Header />
      <Spacer variant="xs" />
      <CertificationKindRadioGroup
        selectedCertificationKind={selectedCertificationKind}
        setSelectedCertificationKind={setSelectedCertificationKind}
      />
      <Spacer variant="xxl" />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.certification.add.intro.header',
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t('headerTitle')} />
    </VStack>
  );
}

function CertificationKindRadioGroup({
  selectedCertificationKind,
  setSelectedCertificationKind,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.certification',
  });

  const CERTIFICATION_KINDS: {
    id: CertificationKind;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: CertificationKind.BASE,
      title: t('certificationKind.base'),
      requiredDetails: [
        {
          iconName: 'SquareDot',
          text: t(
            'add.form.fields.baseCertificationNameAndDisplay.certificationName.label',
          ),
        },
        {
          iconName: 'SquareDot',
          text: t(
            'add.form.fields.baseCertificationClassification.certificationType.label',
          ),
        },
      ],
    },
    {
      id: CertificationKind.SPECIALTY,
      title: t('certificationKind.specialty'),
      requiredDetails: [
        {
          iconName: 'SquareDot',
          text: t(
            'add.form.fields.specialtyNameAndDisplay.specialtyName.label',
          ),
        },
      ],
    },
    {
      id: CertificationKind.HANDLING,
      title: t('certificationKind.handling'),
      requiredDetails: [
        {
          iconName: 'SquareDot',
          text: t('add.form.fields.handlingNameAndDisplay.handlingName.label'),
        },
      ],
    },
  ];

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: CertificationKind) => {
      setSelectedCertificationKind(value);
    },
    [setSelectedCertificationKind],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedCertificationKind) {
      navigate({
        to: '/certification/add/$certificationKind',
        params: { certificationKind: selectedCertificationKind },
        hash:
          selectedCertificationKind === CertificationKind.BASE
            ? AddCertificationStepKeys.BASE_CERTIFICATION_NAME_AND_DISPLAY
            : selectedCertificationKind === CertificationKind.SPECIALTY
              ? AddCertificationStepKeys.SPECIALTY_NAME_AND_DISPLAY
              : AddCertificationStepKeys.HANDLING_NAME_AND_DISPLAY,
      });
    }
  }, [selectedCertificationKind, navigate]);

  const handleAddCertificationHolder = () => {
    navigate({
      to: '/person/add-person-certification',
    });
  };

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4">{t('add.intro.body.selectCertification')}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange}>
        <Grid columns={2} rowGap={28} columnGap={24}>
          {CERTIFICATION_KINDS.map((kind) => (
            <AttributeCard
              key={kind.id}
              title={kind.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(kind.id)}
              actionButton={
                <RadioGroupItem
                  id={kind.id}
                  value={kind.id}
                  className="h-4 w-4"
                  checked={selectedCertificationKind === kind.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t('add.intro.body.itemCard.requiredDetails')}
                </Text>
                {kind.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t('add.intro.body.fillOutDetails')}
      </ActionButton>
      <Spacer variant="xxl" />
      <VStack className="w-full">
        <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
          {t('add.intro.options.description')}
        </Text>
        <Spacer size={24} />
        <ActionButton
          variant="ghost"
          className="relative w-full justify-start p-0"
          onClick={handleAddCertificationHolder}
        >
          <HStack className="items-center justify-between w-full">
            <Icon
              iconName="PlusIcon"
              text={t('add.intro.options.addCertificationHolder')}
              textColor="text-primary"
              colorVariant="primary"
            />
            <Icon iconName="ChevronRight" className="m-[10px]" />
          </HStack>
        </ActionButton>
        <Separator className="my-2" />
      </VStack>
    </>
  );
}
